import { useMutation, DataProxy } from '@apollo/client';
import { useCallback } from 'react';
import { uniqBy } from 'ramda';
import { COUNTRIES } from '@moda/portal-stanchions';
import { PaymentSource } from '../../../constants';
import { BillingAddress, PAYMENT_METHODS_QUERY } from '../usePaymentMethodsQuery';
import {
  CREATE_PAYMENT_METHOD_MUTATION,
  CreatePaymentMethodMutation
} from './createPaymentMethodMutation.rest';

type CardArgs = {
  cardholderName: string;
  nonce: string;
  threeDSecureAuthenticationId: string;
};

type CreateArgs = {
  billingAddress: BillingAddress;
} & CardArgs;

const updateCache = (cache?: DataProxy, data?: CreatePaymentMethodMutation | null) => {
  if (!data || !cache) return;

  let cachedData;

  try {
    cachedData = cache.readQuery({ query: PAYMENT_METHODS_QUERY });
  } catch {
    // if the query is not in the cache, do nothing
  }

  if (!cachedData) return;

  cache.writeQuery({
    query: PAYMENT_METHODS_QUERY,
    data: {
      paymentMethods: {
        __typename: 'RestPaymentMethods',
        data: uniqBy(
          paymentMethod => paymentMethod.id,
          [data.createPaymentMethod.data, ...cachedData.paymentMethods.data]
        )
      }
    }
  });
};

export const useCreatePaymentMethodMutation = () => {
  const [mutate, { loading, error, client }] = useMutation(CREATE_PAYMENT_METHOD_MUTATION);

  const handleCreate = useCallback(
    async ({ billingAddress, ...card }: CreateArgs) => {
      const country = COUNTRIES.find(country => country.alpha2Code === billingAddress.countryCode);
      const encryptedCard = card.nonce;

      if (!country) throw new Error('createPaymentMethodMutation: country not found');

      const result = await mutate({
        variables: {
          input: {
            data: {
              type: 'payments',
              attributes: {
                source: PaymentSource.Braintree,
                cardholder_name: card.cardholderName,
                encrypted_data: encryptedCard,
                threeds_secure_authentication_id: card.threeDSecureAuthenticationId,
                billing_address: {
                  id: billingAddress.id,
                  country_name: country.name,
                  extended_address: billingAddress.extendedAddress,
                  locality: billingAddress.locality,
                  postal_code: billingAddress.postalCode,
                  region: billingAddress.region,
                  street_address: billingAddress.streetAddress
                }
              }
            }
          }
        }
      });

      updateCache(client, result.data);

      return result;
    },
    [client, mutate]
  );

  return [handleCreate, { loading, error }] as const;
};
